/**
 * WhatsApp Setup Utilities
 *
 * Salvataggio della configurazione WhatsApp durante il collegamento del numero.
 * Le chiamate a WAHA passano tutte da wahaProxyService (edge waha-proxy, chiave lato server).
 * 29/10/2026: tolte le vecchie funzioni che chiamavano WAHA dal browser con la chiave
 * (test connessione, creazione sessione, stato, QR, avvio, salvataggio): non avevano piu' chiamanti.
 */

import { supabase } from '../supabase';

export type SessionStatus = 'STOPPED' | 'STARTING' | 'SCAN_QR_CODE' | 'WORKING' | 'FAILED' | 'NOT_FOUND';

/**
 * Crea configurazione preliminare in Supabase per ottenere ID
 * (necessario per costruire URL webhook)
 */
export async function createPreliminaryConfig(
  url: string,
  apiKey: string,
  orgId: string,
  userId?: string,
  forceNew?: boolean
): Promise<string> {
  try {
    const cleanUrl = url.replace(/\/$/, '');

    // Se forceNew, salta il check e crea sempre una nuova config
    if (forceNew) {
      console.log('[WAHA Setup] 🆕 forceNew=true, creo nuova config per user:', userId || 'global');
      const { data, error } = await supabase
        .from('api_configurations')
        .insert({
          service_name: 'waha',
          organization_id: orgId,
          user_id: userId || null,
          api_key: apiKey,
          is_active: true,
          visibility: userId ? 'personal' : 'global',
          configuration: {
            api_url: cleanUrl,
            session: '',
            phone_number: ''
          }
        })
        .select('id')
        .single();

      if (error) {
        console.error('[WAHA Setup] ❌ Errore creazione config (forceNew):', error);
        throw error;
      }

      console.log('[WAHA Setup] ✅ Config preliminare creata (forceNew):', data.id);
      return data.id;
    }

    // 1. Prima controlla se esiste già una config per questo utente
    console.log('[WAHA Setup] 🔍 Controllo config esistente per org:', orgId, 'user:', userId || 'global');

    const query = supabase
      .from('api_configurations')
      .select('id')
      .eq('service_name', 'waha')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })
      .limit(1);

    // Filtra per user_id o null a seconda della modalità
    if (userId) {
      query.eq('user_id', userId);
    } else {
      query.is('user_id', null);
    }

    const { data: existing, error: existingError } = await query.maybeSingle();

    if (existing && !existingError) {
      console.log('[WAHA Setup] ♻️ Config esistente trovata, riutilizzo ID:', existing.id);

      // Aggiorna la config esistente con i nuovi valori
      const { error: updateError } = await supabase
        .from('api_configurations')
        .update({
          api_key: apiKey,
          user_id: userId || null,
          is_active: true,
          visibility: userId ? 'personal' : 'global',
          configuration: {
            api_url: cleanUrl,
            session: '',
            phone_number: ''
          }
        })
        .eq('id', existing.id);

      if (updateError) {
        console.error('[WAHA Setup] ❌ Errore aggiornamento config esistente:', updateError);
        throw updateError;
      }

      return existing.id;
    }

    // 2. Se non esiste, crea nuova config
    console.log('[WAHA Setup] 🆕 Nessuna config esistente, creo nuova per user:', userId || 'global');
    const { data, error } = await supabase
      .from('api_configurations')
      .insert({
        service_name: 'waha',
        organization_id: orgId,
        user_id: userId || null,
        api_key: apiKey,
        is_active: true,
        visibility: userId ? 'personal' : 'global',
        configuration: {
          api_url: cleanUrl,
          session: '',
          phone_number: ''
        }
      })
      .select('id')
      .single();

    if (error) {
      console.error('[WAHA Setup] ❌ Errore creazione config preliminare:', error);
      throw error;
    }

    console.log('[WAHA Setup] ✅ Config preliminare creata:', data.id);
    return data.id;

  } catch (error) {
    console.error('[WAHA Setup] ❌ Errore creazione config:', error);
    throw error;
  }
}

/**
 * Aggiorna configurazione esistente con session e phone
 */
export async function updateWahaConfigWithSession(
  configId: string,
  session: string,
  phoneNumber: string
): Promise<void> {
  try {
    console.log('[WAHA Setup] 📖 Lettura config esistente, ID:', configId);

    // Leggi config esistente completa
    const { data: existingConfig, error: readError } = await supabase
      .from('api_configurations')
      .select('*')
      .eq('id', configId)
      .single();

    if (readError) {
      console.error('[WAHA Setup] ❌ Errore lettura config esistente:', readError);
      throw readError;
    }

    console.log('[WAHA Setup] ✅ Config esistente letta:', {
      id: existingConfig.id,
      service_name: existingConfig.service_name,
      organization_id: existingConfig.organization_id,
      user_id: existingConfig.user_id,
      has_api_key: !!existingConfig.api_key,
      configuration: existingConfig.configuration
    });

    // Merge con nuovi valori
    const updatedConfiguration = {
      ...(existingConfig?.configuration || {}),
      session: session,
      phone_number: phoneNumber
    };

    console.log('[WAHA Setup] 📝 Nuova configuration da salvare:', updatedConfiguration);

    // Aggiorna config
    const { data: updateData, error: updateError } = await supabase
      .from('api_configurations')
      .update({
        configuration: updatedConfiguration,
        is_active: true // ✅ Assicura che config sia attiva dopo setup
      })
      .eq('id', configId)
      .select();

    if (updateError) {
      console.error('[WAHA Setup] ❌ Errore update config:', updateError);
      throw updateError;
    }

    console.log('[WAHA Setup] ✅ Config aggiornata con successo!');
    console.log('[WAHA Setup] 📊 Dati salvati:', updateData);

    // Verifica finale: controlla che la config sia visibile come la cerca Settings.tsx
    const { data: verifyData, error: verifyError } = await supabase
      .from('api_configurations')
      .select('id, service_name, organization_id, user_id, configuration')
      .eq('service_name', 'waha')
      .eq('organization_id', existingConfig.organization_id)
      .is('user_id', null)
      .single();

    if (verifyError) {
      console.error('[WAHA Setup] ⚠️ ATTENZIONE: Config salvata ma non trovata con query Settings:', verifyError);
    } else {
      console.log('[WAHA Setup] ✅ VERIFICA OK: Config trovata con query Settings:', verifyData);
      console.log('[WAHA Setup] 📱 Session salvata:', verifyData.configuration?.session);
      console.log('[WAHA Setup] 📞 Phone salvato:', verifyData.configuration?.phone_number);
    }

    // Invalida cache
    const { data: userData } = await supabase.auth.getUser();
    if (userData?.user?.id) {
      // Ottieni orgId dalla config
      const { data: configData } = await supabase
        .from('api_configurations')
        .select('organization_id')
        .eq('id', configId)
        .single();

      if (configData?.organization_id) {
        const configCacheKey = `whatsapp_config_cache_${userData.user.id}_${configData.organization_id}`;
        sessionStorage.removeItem(configCacheKey);
        console.log('[WAHA Setup] 🧹 Cache invalidata');
      }
    }

  } catch (error) {
    console.error('[WAHA Setup] Errore update config:', error);
    throw error;
  }
}
